import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
// NOTE: Ces composants devront être restylés pour correspondre à l'identité d'Ernest
import { CampaignProgress, ContributionForm, ContributionsList, PaymentModal } from '../../components/crowdfunding';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SocialShare } from '../../components/shared/SocialShare';
import { campaignService, contributionService } from '../../services/crowdfunding';
import { ContributionRequest, Contribution } from '../../types/crowdfunding';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '../../components/ui/use-toast';
// Icônes adaptées
import { ArrowLeft, Calendar, User, ChevronRight, HeartHandshake, BookOpen, Users, Loader2 } from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';

// Le nom du composant est changé pour refléter sa nouvelle fonction
export const ProjetDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const { tenant } = useTenantContext();
  const [isContributing, setIsContributing] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'contributors'>('about');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentContribution, setCurrentContribution] = useState<Contribution | null>(null);
  
  // La logique de récupération des données reste identique
  const { data: project, isLoading: isLoadingProject } = useQuery({
    queryKey: ['project', slug],
    queryFn: async () => {
      if (!slug) throw new Error('Slug du projet non fourni');
      return await campaignService.getCampaignBySlug(slug);
    },
    enabled: !!slug
  });
  
  const { data: contributionsData } = useQuery({
    queryKey: ['contributions', project?.slug],
    queryFn: async () => {
      if (!project?.slug) throw new Error('Slug du projet non disponible');
      return await contributionService.getContributionsBySlug(project.slug);
    },
    enabled: !!project?.slug
  });
  
  const contributions = (contributionsData?.contributions || []).filter(c => c.payment_status === 'paid');
  
  const daysLeft = project ? Math.max(0, differenceInDays(new Date(project.end_date), new Date())) : 0;
  
  // La logique de don reste identique
  const handleDonation = async (data: ContributionRequest) => {
    setIsContributing(true);
    try {
      const contribution = await contributionService.createContribution(data);
      toast({ title: "Soutien initié", description: "Veuillez procéder au paiement pour finaliser votre don." });
      setCurrentContribution(contribution);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Erreur lors du don:', error);
      toast({ title: "Erreur", description: "Une erreur est survenue. Veuillez réessayer.", variant: "destructive" });
    } finally {
      setIsContributing(false);
    }
  };
  
  const handlePaymentSuccess = () => {
    toast({ title: "Merci pour votre soutien !", description: "Votre générosité est la pierre angulaire de cet art." });
    window.location.reload();
  };
  
  if (isLoadingProject) {
    return <PublicLayout><div className="min-h-screen flex justify-center items-center"><Loader2 className="animate-spin h-12 w-12 text-red-500" /></div></PublicLayout>;
  }
  
  if (!project) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex justify-center items-center text-center">
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Projet non trouvé</h2>
            <p className="text-gray-400 mb-6">Le projet que vous cherchez n'existe pas ou a été archivé.</p>
            <Link to="/crowdfunding"><Button variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/10 hover:text-red-400"><ArrowLeft className="mr-2 h-4 w-4" />Retour aux projets</Button></Link>
          </div>
        </div>
      </PublicLayout>
    );
  }
  
  return (
    <PublicLayout>
      <div className="pt-10">
        {currentContribution && <PaymentModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} contribution={currentContribution} onPaymentSuccess={handlePaymentSuccess}/>}
        
        <SEOMetaTags
          title={`${project.title} | Soutenir le Projet`}
          description={project.description}
          url={`https://nzofoug.com/crowdfunding/${project.slug}`}
          image={project.image_url} // Utiliser l'image principale du projet
        />
      
        <div className="min-h-screen relative">
          {/* Fond d'écran cohérent */}
          <div className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover" style={{backgroundImage: tenant ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)', backgroundAttachment: 'fixed'}}>
            <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black/95 backdrop-blur-[2px]"></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-4 py-12">
            {/* Fil d'Ariane */}
            <div className="mb-8">
              <div className="flex items-center text-sm text-gray-400">
                <Link to="/crowdfunding" className="hover:text-red-400 transition-colors">Projets à Soutenir</Link>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-200 truncate max-w-[200px]">{project.title}</span>
              </div>
            </div>
          
            {/* En-tête du projet */}
            <div className="mb-12 flex flex-col items-center text-center">
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">{project.title}</h1>
              <div className="flex flex-col md:flex-row justify-center items-center gap-4 text-gray-300">
                <div className="flex items-center justify-center"><Calendar className="h-4 w-4 mr-2 text-red-400" /><span>Créé {formatDistanceToNow(new Date(project.created_at), { addSuffix: true, locale: fr })}</span></div>
                <div className="flex items-center justify-center"><User className="h-4 w-4 mr-2 text-red-400" /><span>Par Ernest Nzofoug</span></div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Colonne principale */}
              <div className="lg:col-span-2">
                <div className="mb-8 rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
                  <img src={project.image_url || 'https://placehold.co/800x450/1a1a1a/ffffff?text=Projet+Artistique'} alt={project.title} className="w-full h-auto object-cover aspect-video"/>
                </div>
                
                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'about' | 'contributors')} className="mb-8">
                  <TabsList className="grid w-full grid-cols-2 bg-black/50 border border-white/10 p-1 rounded-xl">
                    <TabsTrigger value="about" className="text-gray-300 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg"><BookOpen className="w-4 h-4 mr-2" />À Propos du Projet</TabsTrigger>
                    <TabsTrigger value="contributors" className="text-gray-300 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg"><Users className="w-4 h-4 mr-2" />Mécènes ({contributions.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="about" className="mt-6">
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                      <div className="prose prose-invert max-w-none prose-p:text-gray-200 prose-strong:text-red-400 prose-a:text-red-400 prose-a:underline-offset-4 hover:prose-a:text-red-300 transition-colors">
                        <p className="whitespace-pre-line text-gray-200">{project.description}</p>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="contributors" className="mt-6">
                    <ContributionsList contributions={contributions} showEmpty={true}/>
                  </TabsContent>
                </Tabs>
              </div>
              
              {/* Colonne latérale */}
              <div className="space-y-8 lg:sticky lg:top-24 h-fit">
                {/* Progression du projet */}
                <CampaignProgress goalAmount={project.goal_amount} collectedAmount={project.collected_amount} contributorsCount={contributions.length} daysLeft={daysLeft} />
                
                {/* Formulaire de don (devient la section principale ici) */}
                <div className="bg-white/5 backdrop-blur-sm border border-red-500/20 rounded-xl p-6 shadow-lg">
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center"><HeartHandshake className="w-6 h-6 mr-3 text-red-500" />Soutenir ce Projet</h3>
                  <ContributionForm campaignSlug={project.slug} onSuccess={handleDonation} isLoading={isContributing} suggestedAmounts={[5000, 10000, 25000, 50000, 100000, 250000]} gridCols={2} />
                </div>
                
                {/* Partage social */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-white mb-2">Faire connaître le projet</h3>
                  <p className="text-gray-400 text-sm mb-4">Votre partage est aussi un soutien précieux.</p>
                  <SocialShare description={`Soutenez le projet artistique "${project.title}" d'Ernest Nzofoug !`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

// Export renommé pour la compatibilité
export { ProjetDetail as CampaignDetail };